import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Box,
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  Flex,
  Link,
  Skeleton,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactElement, ReactNode } from 'react';
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import styles from './cardCarousel.module.css';
import type { CardCarouselProps } from './cardCarousel.types';
import { getCardCarouselDimensions } from './cardCarousel.utils';
import { CarouselCard } from './carouselCard/carouselCard';
import { CarouselHeader } from './carouselHeader/carouselHeader';

interface CarouselChildProps {
  ariaLabel?: string;
  width?: number | string;
  widthMobile?: number | string;
}

function renderCarouselChildren(
  children: ReactNode,
  cardWidth: number,
  cardWidthMobile: number | string | undefined
) {
  const allChildren = Children.toArray(children);
  const totalCount = allChildren.filter(isValidElement).length;
  let elementIndex = 0;
  return allChildren.map((child) => {
    if (isValidElement<CarouselChildProps>(child)) {
      const i = elementIndex++;
      const positionLabel = `${i + 1} of ${totalCount}`;
      const existingLabel = child.props.ariaLabel;
      return cloneElement<CarouselChildProps>(
        child as ReactElement<CarouselChildProps>,
        {
          ariaLabel: existingLabel
            ? `${existingLabel}, ${positionLabel}`
            : positionLabel,
          key: child.key || i,
          width: cardWidth,
          widthMobile: cardWidthMobile,
        }
      );
    }
    return child;
  });
}

interface CarouselControlsSkeletonProps {
  showButton?: boolean;
}

function CarouselControlsSkeleton({
  showButton,
}: CarouselControlsSkeletonProps) {
  return (
    <>
      <Flex alignItems="center" className={styles['controlsDesktop']}>
        <Skeleton.Bone height="48px" width="48px" />
        <Skeleton.Bone height="48px" width="48px" ml={2} />
        {showButton && <Skeleton.Bone height="24px" width="90px" ml={12} />}
      </Flex>
      {showButton && (
        <Flex className={styles['controlsMobile']}>
          <Skeleton.Bone height="16px" width="70px" borderRadius="sm" />
        </Flex>
      )}
    </>
  );
}

/**
 * A carousel component that displays a scrollable grid of cards.
 *
 * Scroll position of the carousel is controlled via left-right scroll on mobile, and via a pair of control buttons on desktop.
 * Cards can be displayed in on of 3 different modes. See usage docs for details.
 */
export const CardCarouselComponent = forwardRef(function CardCarouselComponent(
  {
    afterHeader,
    arrowVariant = 'default',
    beforeHeader,
    buttonAriaLabel,
    buttonHref,
    buttonLabel = 'View all',
    buttonOnClick,
    buttonTo,
    controlsColor,
    cardGap: cardGapDesktop = 24,
    cardGapMobile = 16,
    cardHeight = 100,
    cardHeightMobile = cardHeight,
    cardWidth: cardWidthProp,
    cardWidthMobile,
    children,
    className,
    'data-testid': dataTestId,
    headingAs,
    minHeight,
    mode = 'fixed',
    name,
    isLoading = false,
    onMouseEnter,
    onMouseLeave,
    rowCountMobile = 1,
    onScroll,
    hideCarouselControls = false,
    subtitle,
    surtitle,
    ...props
  }: CardCarouselProps,
  ref
) {
  const isProgrammaticScroll = useRef(false);
  const programmaticScrollLeft = useRef(0);
  const [scrollAreaNode, setScrollAreaNode] = useState<null | HTMLDivElement>(
    null
  );

  useImperativeHandle(ref, () => ({
    resetScroll: () => {
      if (scrollAreaNode) {
        scrollAreaNode.scrollTo({
          behavior: 'smooth',
          left: 0,
        });
      }
    },
  }));

  const scrollAreaRef = useCallback((scrollAreaNode: null | HTMLDivElement) => {
    setScrollAreaNode(scrollAreaNode);
  }, []);

  const [width, setWidth] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Watch scrollAreaNode resize and update state
  useEffect(() => {
    if (!scrollAreaNode) return;

    const measureScrollArea = () => {
      setScrollWidth(scrollAreaNode.scrollWidth);
      setWidth(scrollAreaNode.getBoundingClientRect().width);
    };
    measureScrollArea();

    const observer = new ResizeObserver(() => {
      measureScrollArea();
    });
    observer.observe(scrollAreaNode);

    return () => {
      observer.disconnect();
    };
  }, [scrollAreaNode]);

  useEffect(() => {
    if (!scrollAreaNode) return;

    const setScroll = () => {
      if (isProgrammaticScroll.current) {
        scrollAreaNode.scrollLeft = programmaticScrollLeft.current;
        isProgrammaticScroll.current = false;
      }
      setScrollLeft(scrollAreaNode.scrollLeft);
    };

    scrollAreaNode.addEventListener('scrollend', setScroll);

    return () => {
      scrollAreaNode.removeEventListener('scrollend', setScroll);
    };
  }, [scrollAreaNode]);

  // Calculate tablet / desktop `cardWidth` and `cardGap` based on `mode`
  const { cardGap, cardWidth } = getCardCarouselDimensions(
    mode,
    cardWidthProp,
    cardGapDesktop,
    width
  );

  // Trigger left or right scroll
  const scrollCarousel = (direction = 'right') => {
    if (!scrollAreaNode) {
      return;
    }
    const scrollModifier = direction === 'right' ? 1 : -1;

    isProgrammaticScroll.current = true;
    programmaticScrollLeft.current =
      scrollAreaNode.scrollLeft + scrollModifier * width;
    scrollAreaNode.scrollBy({
      behavior: 'smooth',
      left: scrollModifier * width,
    });

    onScroll && onScroll(direction);
  };

  // Titles, button controls
  const headerContent = (
    <CarouselHeader
      afterHeader={afterHeader}
      beforeHeader={beforeHeader}
      headingAs={headingAs}
      name={name}
      isLoading={isLoading}
      subtitle={subtitle}
      surtitle={surtitle}
    >
      {!isLoading && !hideCarouselControls && (
        <Flex
          alignItems="center"
          className={styles['controlsDesktop']}
          gap={arrowVariant === 'filled' ? 16 : undefined}
        >
          <Button.Icon
            aria-controls="carousel-scroll-area"
            aria-label={`Go back on ${name} carousel`}
            className={
              arrowVariant === 'filled' ? styles['arrowFilled'] : undefined
            }
            disabled={scrollLeft === 0}
            icon={
              arrowVariant === 'filled' ? (
                <ArrowLeftIcon />
              ) : (
                <ChevronLeftIcon />
              )
            }
            onClick={() => scrollCarousel('left')}
          />
          <Button.Icon
            aria-controls="carousel-scroll-area"
            aria-label={`Go forward on ${name} carousel`}
            className={
              arrowVariant === 'filled' ? styles['arrowFilled'] : undefined
            }
            disabled={
              // NOTE! Chrome rounding bug requires use of Math.floor / Math.ceil here
              Math.ceil(scrollLeft) + Math.ceil(width) >=
              Math.floor(scrollWidth)
            }
            icon={
              arrowVariant === 'filled' ? (
                <ArrowRightIcon />
              ) : (
                <ChevronRightIcon />
              )
            }
            onClick={() => scrollCarousel('right')}
          />
          {(buttonHref || buttonTo || buttonOnClick) && (
            <Link
              className={styles['viewAllLink']}
              ariaLabel={buttonAriaLabel || `${buttonLabel} ${name}`}
              color={controlsColor}
              ml={12}
              onClick={buttonOnClick}
              href={buttonHref}
              to={buttonTo}
              typography="body"
            >
              {buttonLabel}
            </Link>
          )}
        </Flex>
      )}
      {!isLoading && (buttonHref || buttonTo || buttonOnClick) && (
        <Flex className={styles['controlsMobile']}>
          <Link
            className={styles['link']}
            ariaLabel={buttonAriaLabel || `${buttonLabel} ${name}`}
            color={controlsColor}
            onClick={buttonOnClick}
            href={buttonHref}
            to={buttonTo}
            typography="body"
          >
            {buttonLabel}
          </Link>
        </Flex>
      )}
      {isLoading && (
        <CarouselControlsSkeleton
          showButton={!!(buttonHref || buttonTo || buttonOnClick)}
        />
      )}
    </CarouselHeader>
  );

  // Carousel cards
  const scrollAreaContent = (
    <div
      id="carousel-scroll-area"
      ref={scrollAreaRef}
      className={clsx(styles['scrollArea'], {
        [styles['scrollArea_noScroll']]: isLoading,
      })}
    >
      {/* Boolean check fixes a Chrome bug, where the carousel loads scrolled to random card */}
      {/* The initial width measurement of the scrollArea + cardWidth / cardGap calculations must occur before cards are rendered */}
      {!!cardWidth && !!cardGap && (
        <Box
          className={styles['grid']}
          style={{
            '--carousel-card-gap': `${cardGap}px`,
            '--carousel-card-gap-mobile': `${cardGapMobile}px`,
            '--carousel-card-width': `${cardWidth}px`,
            '--carousel-card-width-mobile': `${cardWidthMobile}px`,
            '--carousel-row-count-mobile': `${rowCountMobile}`,
            '--carousel-card-grid-template-rows':
              Array(rowCountMobile).join('auto ') + 'auto',
          }}
        >
          {!isLoading &&
            renderCarouselChildren(children, cardWidth, cardWidthMobile)}
          {isLoading &&
            Array.from({ length: 20 }, (_, i) => (
              <CarouselCard
                width={cardWidth}
                widthMobile={cardWidthMobile}
                key={i}
              >
                <Skeleton.Bone
                  className={styles['skeletonBone']}
                  key={i}
                  style={{
                    '--carousel-skeleton-height': `${cardHeight}px`,
                    '--carousel-skeleton-height-mobile': `${cardHeightMobile}px`,
                  }}
                />
              </CarouselCard>
            ))}
        </Box>
      )}
    </div>
  );

  return (
    <Box
      as="section"
      className={className}
      data-testid={dataTestId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      width="100%"
      minHeight={minHeight}
      {...props}
    >
      {headerContent}
      {scrollAreaContent}
    </Box>
  );
});

export const CardCarousel = Object.assign(CardCarouselComponent, {
  Card: CarouselCard,
  Header: CarouselHeader,
});
